import React, { useRef, useState, useEffect } from 'react';

const SignupProfessionalInfo = ({ formData, errors, handleChange, prevStep, nextStep }) => {
  const cvFileInputRef = useRef(null);
  const [cvFile, setCvFile] = useState(null);
  const [isExpertiseDropdownOpen, setIsExpertiseDropdownOpen] = useState(false);
  const [isSubjectsDropdownOpen, setIsSubjectsDropdownOpen] = useState(false);

  const expertiseOptions = [
    'CSS',
    'PMS',
    'PCS',
    'Judiciary Exam',
    'FPSC One Paper Exams',
    'PPSC',
    'KPPSC',
    'SPSC',
    'BPSC'
  ];

  const subjectsOptions = [
   'English Essay',
    'English Precis and Composition',
    'General Science and Ability',
    'Current Affairs',
    'Pakistan Affairs',
    'Islamic Studies OR Comparative Study of Major Religions (For Non Muslims)',
    'Accountancy and Auditing',
    'Economics',
    'Computer Science',
    'Political Science',
    'International Relations',
    'Physics',
    'Chemistry',
    'Applied Mathematics',
    'Pure Mathematics',
    'Statistics',
    'Geology',
    'Business Administration',
    'Public Administration',
    'Governance and Public Policies',
    'Town Planning and Urban Management',
    'History of Pakistan and India',
    'Islamic History and Culture',
    'British History',
    'European History',
    'History of USA',
    'Gender Studies',
    'Environmental Sciences',
    'Agriculture & Forestry',
    'Botany',
    'Zoology',
    'English Literature',
    'Urdu Literature',
    'Law',
    'Constitutional Law',
    'International Law',
    'Muslim Law & Jurisprudence',
    'Criminology',
    'Philosophy',
    'Journalism & Mass Communication',
    'Psychology',
    'Geography',
    'Sociology',
    'Anthropology',
    'Punjabi',
    'Sindhi',
    'Pashto',
    'Balochi',
    'Persian',
    'Arabic',
    'Urdu',
    'General Knowledge'
  ];

  // Initialize CV file from formData when component mounts
  useEffect(() => {
    if (formData.professional.cv && !cvFile) {
      setCvFile(formData.professional.cv);
    }
  }, [formData.professional.cv, cvFile]);

  const handleCvClick = () => {
    cvFileInputRef.current?.click();
  };

  const handleCvChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      const validTypes = [
        'application/pdf', 
        'application/msword', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      const validExtensions = ['.pdf', '.doc', '.docx'];
      
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      const isValidType = validTypes.includes(file.type) || validExtensions.includes(fileExtension);
      
      if (!isValidType) {
        alert('Please upload only PDF or Word documents (.pdf, .doc, .docx)');
        return;
      }
      
      // File size check removed - any size allowed
      
      console.log('File selected:', file.name, file.size, file.type);
      setCvFile(file);
      
      // Also update the form data so it gets included in the form submission
      const event = {
        target: {
          name: 'professional.cv',
          value: file
        }
      };
      handleChange(event);
      
      // Clear CV error if any
      if (errors['professional.cv']) {
        const errorEvent = {
          target: {
            name: 'professional.cv',
            value: ''
          }
        };
        handleChange(errorEvent);
      }
    }
  };

  const handleRemoveCv = () => {
    setCvFile(null);
    
    // Also update the form data
    const event = {
      target: {
        name: 'professional.cv',
        value: null
      }
    };
    handleChange(event);
  };

  const handleExpertiseSelect = (option) => {
    const currentExpertise = formData.professional.areaOfExpertise ? formData.professional.areaOfExpertise.split(', ') : [];
    
    if (currentExpertise.includes(option)) {
      // Remove if already selected
      const updatedExpertise = currentExpertise.filter(item => item !== option);
      const event = {
        target: {
          name: 'professional.areaOfExpertise',
          value: updatedExpertise.join(', ')
        }
      };
      handleChange(event);
    } else {
      // Add if not selected
      const updatedExpertise = [...currentExpertise, option];
      const event = {
        target: {
          name: 'professional.areaOfExpertise',
          value: updatedExpertise.join(', ')
        }
      };
      handleChange(event);
    }
  };

  const removeExpertiseItem = (itemToRemove) => {
    const currentExpertise = formData.professional.areaOfExpertise.split(', ').filter(item => item !== itemToRemove);
    const event = {
      target: {
        name: 'professional.areaOfExpertise',
        value: currentExpertise.join(', ')
      }
    };
    handleChange(event);
  };

  const handleSubjectSelect = (option) => {
    const currentSubjects = formData.professional.department ? formData.professional.department.split(', ') : [];
    
    if (currentSubjects.includes(option)) {
      // Remove if already selected
      const updatedSubjects = currentSubjects.filter(item => item !== option);
      const event = {
        target: {
          name: 'professional.department',
          value: updatedSubjects.join(', ')
        }
      };
      handleChange(event);
    } else {
      // Add if not selected
      const updatedSubjects = [...currentSubjects, option];
      const event = {
        target: {
          name: 'professional.department',
          value: updatedSubjects.join(', ')
        }
      };
      handleChange(event);
    }
  };

  const removeSubjectItem = (itemToRemove) => {
    const currentSubjects = formData.professional.department.split(', ').filter(item => item !== itemToRemove);
    const event = {
      target: {
        name: 'professional.department',
        value: currentSubjects.join(', ')
      }
    };
    handleChange(event);
  };

  return (
    <div className="space-y-4 lg:space-y-5">
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-gray-900">Professional Information</h3>
        <p className="text-gray-600">Share your professional background</p>
      </div>

      {/* Area of Expertise - Moved to top */}
      <div className="group">
        <label htmlFor="professional.areaOfExpertise" className="block text-sm font-semibold text-gray-700 mb-2">
          Area of Expertise *
        </label>
        
        {/* Custom Multi-Select Dropdown for Expertise */}
        <div className="relative">
          <div
            onClick={() => setIsExpertiseDropdownOpen(!isExpertiseDropdownOpen)}
            className={`w-full px-4 py-3 text-base border-2 rounded-xl cursor-pointer focus:outline-none focus:ring-2 transition-all duration-300 bg-white shadow-sm ${
              errors['professional.areaOfExpertise'] 
                ? 'border-red-300 focus:border-red-500 focus:ring-red-100' 
                : 'border-gray-200 focus:border-green-500 focus:ring-green-100'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex flex-wrap gap-2">
                {formData.professional.areaOfExpertise ? (
                  formData.professional.areaOfExpertise.split(', ').map((item, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-lg bg-green-100 text-green-800 text-sm"
                    >
                      {item}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeExpertiseItem(item);
                        }}
                        className="ml-1 text-green-600 hover:text-green-800"
                      >
                        ×
                      </button>
                    </span>
                  ))
                ) : (
                  <span className="text-gray-400">Select expertise areas</span>
                )}
              </div>
              <svg
                className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                  isExpertiseDropdownOpen ? 'transform rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {/* Dropdown Options for Expertise */}
          {isExpertiseDropdownOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
              {expertiseOptions.map((option) => {
                const isSelected = formData.professional.areaOfExpertise?.split(', ').includes(option);
                return (
                  <div
                    key={option}
                    onClick={() => handleExpertiseSelect(option)}
                    className={`px-4 py-2 cursor-pointer hover:bg-green-50 transition-colors duration-200 ${
                      isSelected ? 'bg-green-100 text-green-800 font-medium' : 'text-gray-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="mr-3 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      {option}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {errors['professional.areaOfExpertise'] && (
          <p className="mt-1 text-sm text-red-600">{errors['professional.areaOfExpertise']}</p>
        )}
        <p className="mt-1 text-xs text-gray-500">You can select multiple options</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="group">
          <label htmlFor="professional.department" className="block text-sm font-semibold text-gray-700 mb-2">
            Subjects *
          </label>
          
          {/* Custom Multi-Select Dropdown for Subjects */}
          <div className="relative">
            <div
              onClick={() => setIsSubjectsDropdownOpen(!isSubjectsDropdownOpen)}
              className={`w-full px-4 py-3 text-base border-2 rounded-xl cursor-pointer focus:outline-none focus:ring-2 transition-all duration-300 bg-white shadow-sm ${
                errors['professional.department'] 
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-100' 
                  : 'border-gray-200 focus:border-green-500 focus:ring-green-100'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-2">
                  {formData.professional.department ? (
                    formData.professional.department.split(', ').map((item, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-lg bg-green-100 text-green-800 text-sm"
                      >
                        {item}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeSubjectItem(item);
                          }}
                          className="ml-1 text-green-600 hover:text-green-800"
                        >
                          ×
                        </button>
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400">Select subjects</span>
                  )}
                </div>
                <svg
                  className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                    isSubjectsDropdownOpen ? 'transform rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* Dropdown Options for Subjects */}
            {isSubjectsDropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                {subjectsOptions.map((option) => {
                  const isSelected = formData.professional.department?.split(', ').includes(option);
                  return (
                    <div
                      key={option}
                      onClick={() => handleSubjectSelect(option)}
                      className={`px-4 py-2 cursor-pointer hover:bg-green-50 transition-colors duration-200 ${
                        isSelected ? 'bg-green-100 text-green-800 font-medium' : 'text-gray-700'
                      }`}
                    >
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className="mr-3 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        {option}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {errors['professional.department'] && (
            <p className="mt-1 text-sm text-red-600">{errors['professional.department']}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">You can select multiple subjects</p>
        </div>

        <div className="group">
          <label htmlFor="professional.experience" className="block text-sm font-semibold text-gray-700 mb-2">
            Years of Experience *
          </label>
          <select
            id="professional.experience"
            name="professional.experience"
            required
            value={formData.professional.experience}
            onChange={handleChange}
            className={`w-full px-4 py-3 text-base border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 bg-white shadow-sm ${
              errors['professional.experience'] 
                ? 'border-red-300 focus:border-red-500 focus:ring-red-100' 
                : 'border-gray-200 focus:border-green-500 focus:ring-green-100'
            }`}
          >
            <option value="">Select experience</option>
            <option value="0-2">0-2 years</option>
            <option value="3-5">3-5 years</option>
            <option value="6-10">6-10 years</option>
            <option value="10+">10+ years</option>
          </select>
          {errors['professional.experience'] && (
            <p className="mt-1 text-sm text-red-600">{errors['professional.experience']}</p>
          )}
        </div>
      </div>

      <div className="group">
        <label htmlFor="professional.website" className="block text-sm font-semibold text-gray-700 mb-2">
          Website or Portfolio Link
        </label>
        <input
          id="professional.website"
          name="professional.website"
          type="url"
          value={formData.professional.website}
          onChange={handleChange}
          className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-100 focus:border-green-500 transition-all duration-300 bg-white shadow-sm"
          placeholder="https://yourportfolio.com"
        />
      </div>

      <div className="group">
        <label htmlFor="professional.youtubeVideoUrl" className="block text-sm font-semibold text-gray-700 mb-2">
          YouTube Video URL (Optional)
        </label>
        <input
          id="professional.youtubeVideoUrl"
          name="professional.youtubeVideoUrl"
          type="url"
          value={formData.professional.youtubeVideoUrl}
          onChange={handleChange}
          className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-100 focus:border-green-500 transition-all duration-300 bg-white shadow-sm"
          placeholder="https://youtube.com/your-teaching-video"
        />
        {errors['professional.youtubeVideoUrl'] && (
          <p className="mt-1 text-sm text-red-600">{errors['professional.youtubeVideoUrl']}</p>
        )}
      </div>

      {/* CV Upload Section - Now Mandatory with no size limit */}
      <div className="group">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Upload CV *
        </label>
        <div className="flex flex-col items-center space-y-4">
          <div className="relative w-full">
            <div 
              className={`w-full min-h-[120px] rounded-2xl border-2 border-dashed cursor-pointer transition-all duration-300 flex flex-col items-center justify-center p-6 ${
                cvFile 
                  ? 'border-green-500 bg-green-50' 
                  : errors['professional.cv']
                  ? 'border-red-300 bg-red-50'
                  : 'border-gray-300 bg-gray-50 hover:border-green-400 hover:bg-green-50'
              }`}
              onClick={handleCvClick}
            >
              {cvFile ? (
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                    {cvFile.type.includes('pdf') || cvFile.name.toLowerCase().endsWith('.pdf') ? (
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    ) : (
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    )}
                  </div>
                  <p className="text-sm font-medium text-green-700 truncate max-w-xs">
                    {cvFile.name}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {(cvFile.size / 1024 / 1024).toFixed(2)} MB • {
                      cvFile.type.includes('pdf') || cvFile.name.toLowerCase().endsWith('.pdf') ? 'PDF Document' : 'Word Document'
                    }
                  </p>
                  <p className="text-xs text-green-600 font-medium mt-2">
                    ✓ CV uploaded successfully
                  </p>
                </div>
              ) : (
                <div className="text-center p-4">
                  <svg className={`w-12 h-12 mx-auto mb-3 ${
                    errors['professional.cv'] ? 'text-red-400' : 'text-gray-400'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className={`text-sm ${
                    errors['professional.cv'] ? 'text-red-500' : 'text-gray-600'
                  } mb-1`}>
                    Click to upload CV
                  </p>
                  <p className="text-xs text-gray-500">
                    PDF or Word document 
                  </p>
                </div>
              )}
            </div>
            
            {cvFile && (
              <button
                type="button"
                onClick={handleRemoveCv}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 transition-colors duration-200 shadow-lg"
              >
                ×
              </button>
            )}
          </div>

          <input
            ref={cvFileInputRef}
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleCvChange}
            className="hidden"
          />

          <div className="text-center">
            <button
              type="button"
              onClick={handleCvClick}
              className={`text-sm font-medium transition-colors duration-200 ${
                errors['professional.cv'] 
                  ? 'text-red-600 hover:text-red-700' 
                  : 'text-green-600 hover:text-green-700'
              }`}
            >
              {cvFile ? 'Change CV' : 'Upload CV'}
            </button>
          </div>

          {errors['professional.cv'] && (
            <div className="text-center">
              <p className="text-xs text-red-600 font-medium">
                {errors['professional.cv']}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="flex space-x-4">
        <button
          type="button"
          onClick={prevStep}
          className="flex-1 py-3 px-6 text-base font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
        >
          Back
        </button>
        <button
          type="button"
          onClick={nextStep}
          className="flex-1 py-3 px-6 text-base font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
        >
          Next: Create Password
        </button>
      </div>
    </div>
  );
};

export default SignupProfessionalInfo;